import { cn } from '@/trailmix/lib/cn';
import { mdiEyeOutline, mdiInformationOutline, mdiTextBox } from '@mdi/js';
import Icon from '@mdi/react';
import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

const proseClasses =
    'prose prose-base prose-quoteless prose-p:mt-0 prose-p:mb-3 prose-ul:my-1 prose-ol:my-1 prose-li:m-0 prose-blockquote:my-4 prose-hr:my-2 prose-pre:my-2';

const HINTS: [string, string][] = [
    ['**bold**', 'Bold'],
    ['*italic*', 'Italic'],
    ['~~strike~~', 'Strikethrough'],
    ['- item', 'Bullet list'],
    ['> text', 'Blockquote'],
];

export function Tiptap({
    id,
    value,
    onChange,
    rows,
}: {
    id?: string;
    value: string;
    onChange: (value: string) => void;
    rows?: number;
}) {
    const [preview, setPreview] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    }, [value, preview]);

    return (
        <div className='rounded-md border border-gray-200 bg-white'>
            <div className='flex items-center justify-between rounded-t-md border-b border-gray-200 bg-gray-50 px-3 py-2'>
                <div className='flex items-center gap-3'>
                    {(
                        [
                            ['Write', mdiTextBox, false],
                            ['Preview', mdiEyeOutline, true],
                        ] as const
                    ).map(([label, icon, isPreview]) => (
                        <button
                            key={label}
                            type='button'
                            onClick={() => setPreview(isPreview)}
                            className={cn(
                                'flex items-center gap-1.5 text-sm transition-colors',
                                preview === isPreview
                                    ? 'font-semibold text-gray-800'
                                    : 'font-medium text-gray-500 hover:text-gray-600'
                            )}
                        >
                            <Icon path={icon} className='size-4' />
                            {label}
                        </button>
                    ))}
                </div>
                <div className='group relative'>
                    <button
                        type='button'
                        className='flex items-center text-gray-400 transition-colors hover:text-gray-600'
                    >
                        <Icon path={mdiInformationOutline} className='size-5' />
                    </button>
                    <div className='absolute right-0 top-full z-10 mt-2 hidden w-52 rounded-md border border-gray-200 bg-white p-3 shadow-lg group-hover:block'>
                        <p className='mb-2 text-xs font-medium text-gray-500'>Markdown</p>
                        <div className='space-y-1.5'>
                            {HINTS.map(([syntax, label]) => (
                                <div key={syntax} className='flex items-center justify-between gap-4'>
                                    <code className='rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700'>
                                        {syntax}
                                    </code>
                                    <span className='text-xs text-gray-400'>{label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            {preview ? (
                <div className={cn(proseClasses, 'min-h-24 px-3 py-2.5')}>
                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{value}</ReactMarkdown>
                </div>
            ) : (
                <textarea
                    ref={textareaRef}
                    id={id}
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    style={{ minHeight: rows ? `${rows * 1.5}rem` : '96px' }}
                    className='w-full resize-none overflow-hidden rounded-b-md border-none bg-transparent px-3 py-2.5 text-base focus:outline-none'
                />
            )}
        </div>
    );
}

export function ProseDisplay({ content, className }: { content: string; className?: string }) {
    return (
        <div className={cn('max-w-full', proseClasses, className)}>
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{content}</ReactMarkdown>
        </div>
    );
}
